import { useEffect, useState } from "react";

// Offre de livraison du pays de la visiteuse, estimée par le fuseau horaire du navigateur (aucune IP ni cookie).
// Les seuils viennent du backend (/api/shipping/geo, miroir de SHIPPING) : rien n'est codé en dur ici.
let geoCache = null;
let pending = null;

export function useShipGeo() {
  const [geo, setGeo] = useState(geoCache);
  useEffect(() => {
    if (geoCache || !process.env.REACT_APP_BACKEND_URL) return undefined;
    let alive = true;
    let tz = "";
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { tz = ""; }
    pending = pending || fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/geo?tz=${encodeURIComponent(tz)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { geoCache = d; return d; })
      .catch(() => null);
    pending.then((d) => { if (alive && d) setGeo(d); });
    return () => { alive = false; };
  }, []);
  return geo;
}

export const countryName = (cc, lang) => {
  try { return new Intl.DisplayNames([lang], { type: "region" }).of(cc); } catch { return cc; }
};

export const geoEuro = (n, en) => {
  const s = Number(n).toFixed(2).replace(/\.00$/, "");
  return en ? `€${s}` : `${s.replace(".", ",")} €`;
};

const isAbroad = (geo) => geo && (geo.zone === "uk_ch" || geo.zone === "world" || geo.zone === "unknown");

/** Message du bandeau pour les visiteuses hors France / DOM-TOM (null sinon). */
export function geoMessage(geo, lang) {
  const en = lang === "en";
  if (!geo || geo.zone === "fr" || geo.zone === "domtom" || (geo.zone === "unknown" && !en)) return null;
  const name = geo.country ? countryName(geo.country, lang) : null;
  if (geo.zone === "eu") {
    return en ? `${name}: Mondial Relay pickup point delivery ${geoEuro(geo.from_price, en)}` : `${name} : livraison en Point Relais ${geoEuro(geo.from_price, en)}`;
  }
  const where = name || (en ? "Worldwide" : "Monde entier");
  return en ? `${where}: free tracked shipping from ${geoEuro(geo.free_from, en)}` : `${where} : livraison offerte dès ${geoEuro(geo.free_from, en)} d'achat`;
}

/** Livraison pour le panier : texte + progression vers la livraison offerte quand un seuil existe. */
export function cartShipping(geo, lang, subtotal) {
  const en = lang === "en";
  if (!geo || geo.zone === "fr" || (geo.zone === "unknown" && !en)) {
    return { text: en ? "Free pickup-point delivery in France · other countries: rate at the next step" : "Livraison offerte en Point Relais / Locker en France — tarif selon votre zone à l'étape suivante" };
  }
  const name = geo.country ? countryName(geo.country, lang) : en ? "your country" : "votre pays";
  if (geo.zone === "eu") return { text: en ? `${name}: Mondial Relay pickup point ${geoEuro(geo.from_price, en)}` : `${name} : Point Relais Mondial Relay ${geoEuro(geo.from_price, en)}` };
  if (geo.zone === "domtom") return { text: en ? "Overseas France: Colissimo from €11.90" : "DOM-TOM : Colissimo 11,90 € jusqu'à 30 € d'achat, puis 14,99 €" };
  if (!isAbroad(geo) || !geo.free_from) return { text: "" };
  const left = Math.max(0, geo.free_from - subtotal);
  const progress = Math.min(1, subtotal / geo.free_from);
  if (!left) return { progress, reached: true, text: en ? `Free shipping to ${name} unlocked` : `Livraison offerte vers ${name} débloquée` };
  return { progress, text: en ? `Add ${geoEuro(left, en)} for free shipping to ${name}` : `Plus que ${geoEuro(left, en)} pour la livraison offerte vers ${name}` };
}

/** Courte mention sous le prix d'une fiche produit. */
export function productShipNote(geo, lang) {
  const en = lang === "en";
  if (!geo || geo.zone === "fr" || (geo.zone === "unknown" && !en)) return en ? "Incl. VAT · free pickup-point delivery in France" : "Prix TTC · Livraison offerte en Point Relais ou Locker";
  const name = geo.country ? countryName(geo.country, lang) : null;
  if (geo.zone === "eu") return en ? `Incl. VAT · ${name}: pickup point ${geoEuro(geo.from_price, en)}` : `Prix TTC · ${name} : Point Relais ${geoEuro(geo.from_price, en)}`;
  if (geo.zone === "domtom") return en ? "Incl. VAT · Overseas France from €11.90" : "Prix TTC · DOM-TOM dès 11,90 €";
  const where = name || (en ? "worldwide" : "dans le monde");
  return en ? `Incl. VAT · free tracked shipping to ${where} from ${geoEuro(geo.free_from, en)}` : `Prix TTC · livraison offerte vers ${where} dès ${geoEuro(geo.free_from, en)}`;
}
